import React from "react";
import PropTypes from "prop-types";
import { withStyles } from "@material-ui/core/styles";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import utils from "../../universal/utils";
import commons from "../common/commons";

const styles = (theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  section: {
    marginTop: theme.spacing(3),
  },
  loading: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  mono: {
    fontFamily: "monospace",
  },
  productCell: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    minWidth: 220,
  },
  thumb: {
    width: 48,
    height: 48,
    objectFit: "contain",
    backgroundColor: "#f6f7f8",
    borderRadius: 4,
    flexShrink: 0,
  },
  productBrand: {
    fontWeight: 600,
    fontSize: 13,
    lineHeight: 1.3,
  },
  productMeta: {
    color: "#667085",
    fontSize: 12,
    lineHeight: 1.4,
  },
  card: {
    display: "flex",
    gap: 12,
    alignItems: "flex-start",
    marginTop: 12,
    padding: 16,
    border: "1px solid rgba(0,0,0,0.12)",
    borderRadius: 8,
  },
  cardMuted: {
    backgroundColor: "#fafafa",
  },
  cardThumb: {
    width: 72,
    height: 72,
    objectFit: "contain",
    backgroundColor: "#f6f7f8",
    borderRadius: 4,
    flexShrink: 0,
  },
  reasons: {
    margin: "8px 0 0",
    paddingInlineStart: 18,
  },
  facts: {
    marginTop: 8,
    color: "#475467",
    fontSize: 13,
    lineHeight: 1.6,
  },
});

const labels = {
  title: "סוכן תמחור",
  subtitle: "רץ ידנית או כל 3, 7 או 10 ימים, מציע שינויי מחיר, ומאפשר להחיל או לבטל אותם.",
  from: "מתאריך",
  to: "עד תאריך",
  baselineFrom: "בסיס מתאריך",
  baselineTo: "בסיס עד תאריך",
  limit: "מגבלת מוצרים",
  maxChange: "שינוי מקסימלי %",
  competitors: "כלול בדיקות מתחרים (עדיין לא פעיל)",
  runNow: "הרץ עכשיו",
  refresh: "רענון",
  runsTitle: "היסטוריית הרצות",
  created: "נוצר",
  status: "סטטוס",
  trigger: "מקור",
  range: "טווח",
  action: "פעולה",
  view: "צפייה",
  loadingRuns: "טוען הרצות...",
  noRuns: "אין עדיין הרצות",
  runDetails: "פירוט הרצה",
  summary: "סיכום",
  applySelected: "החל נבחרים",
  loadingRun: "טוען הרצה...",
  product: "מוצר",
  ean: 'מק"ט',
  current: "מחיר נוכחי",
  suggested: "מחיר מוצע",
  confidence: "ביטחון",
  unitNet: "רווח נקי (₪)",
  targetNet: "יעד רווח",
  lastChange: "שינוי אחרון",
  vitals: "לפני ← אחרי",
  pastPeak: "שיא עבר",
  stock: "מלאי",
  noRecommendations: "אין המלצות",
  applyHistory: "היסטוריית החלות",
  noApplyHistory: "אין עדיין שינויים שהוחלו",
  rollback: "ביטול החלה",
  changes: "שינויים",
  rolledBack: "בוטל",
  orders: "הזמנות",
  days: "ימים",
  month: "חודש",
  floor: "רצפה",
  statusMap: { running: "רץ", completed: "הושלם", failed: "נכשל" },
  triggerMap: { manual: "ידני", cron: "חיצוני", schedule: "לוח זמנים" },
  actionMap: { decrease: "הורדה", increase: "העלאה", keep: "השארה", skip: "דילוג" },
  outcomeMap: {
    too_soon: "מוקדם למדוד",
    improved: "השתפר אחרי השינוי",
    no_exposure: "בלי חשיפה",
    flat_or_down: "לא השתפר",
    no_prior_change: "אין שינוי קודם",
  },
  scheduleTitle: "הרצה אוטומטית",
  scheduleHelp: "ההרצה הראשונה בשעה שנבחרה לפי שעון ישראל, ואחר כך כל 3, 7 או 10 ימים. אין הרצה יומית, כדי שיהיה זמן לאסוף נתונים.",
  scheduleEnabled: "הפעל הרצה אוטומטית",
  scheduleEvery: "כל כמה זמן",
  scheduleTime: "שעה",
  scheduleSave: "שמור לוח זמנים",
  scheduleNext: "ההרצה הבאה",
  scheduleOff: "לוח הזמנים כבוי",
  scheduleMail: "אם הרצה מתוזמנת תיצור שינויי מחיר, תישלח הודעה אל",
  every3: "כל 3 ימים",
  every7: "כל 7 ימים",
  every10: "כל 10 ימים",
  selectAll: "בחר הכל",
  clearSelection: "נקה בחירה",
  why: "למה",
  noReason: "אין הסבר שמור להמלצה הזו.",
  views: "צפיות",
  adds: "הוספות לעגלה",
  checkouts: "כניסות לקופה",
  selectedCount: "נבחרו",
  proposed: "שינויים מוצעים",
};

function mapped(map, value) {
  if (!value) return "—";
  return map[value] || value;
}

function productImageUrl(rec) {
  const product = rec.snapshot?.product || {};
  return commons.getProductPrimaryImage(product.cdn, rec.ean).url;
}

function isActionable(rec) {
  return (
    rec.action !== "skip" &&
    rec.action !== "keep" &&
    typeof rec.suggestedPrice === "number" &&
    rec.suggestedPrice !== rec.currentPrice
  );
}

function countsLine(counts) {
  if (!counts) return null;
  return [
    `${counts.orders || 0} ${labels.orders}`,
    `${counts.addToCart || 0} ${labels.adds}`,
    `${counts.checkoutInit || 0} ${labels.checkouts}`,
    `${counts.impressions || 0} ${labels.views}`,
  ].join(" · ");
}

const basePath = utils.getEdgeBasePath();

function formatDateISO(d) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function defaultRanges() {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const fromD = new Date(today);
  fromD.setDate(fromD.getDate() - 6);

  const baselineFrom = new Date(today);
  baselineFrom.setDate(baselineFrom.getDate() - 30);
  const baselineTo = new Date(today);
  baselineTo.setDate(baselineTo.getDate() - 7);

  return {
    from: formatDateISO(fromD),
    to: formatDateISO(today),
    baselineFrom: formatDateISO(baselineFrom),
    baselineTo: formatDateISO(baselineTo),
  };
}

class PricingAgentPage extends React.Component {
  constructor(props) {
    super(props);
    const ranges = defaultRanges();
    this.state = {
      from: ranges.from,
      to: ranges.to,
      baselineFrom: ranges.baselineFrom,
      baselineTo: ranges.baselineTo,
      limit: 80,
      includeCompetitors: false,
      maxChangePct: 8,
      runs: [],
      runsLoading: true,
      selectedRunId: null,
      selectedRun: null,
      runLoading: false,
      selection: {},
      applyLoading: false,
      error: null,
      schedule: {
        enabled: false,
        intervalDays: 7,
        time: "09:00",
        nextRunAt: null,
        lastRunAt: null,
        notifyEmail: "itamar.arjuan@gmail.com",
      },
      scheduleSaving: false,
    };
  }

  componentDidMount() {
    this.loadRuns();
    this.loadSchedule();
  }

  loadRuns = async () => {
    this.setState({ runsLoading: true, error: null });
    try {
      const res = await fetch(`${basePath}/api/v1/admin/pricing-agent/runs?page=1&limit=25`);
      const json = await res.json();
      if (!json.ok) throw new Error(json.error || "טעינת ההרצות נכשלה");
      this.setState({ runs: json.items || [] });
    } catch (e) {
      this.setState({ error: e.message || String(e) });
    } finally {
      this.setState({ runsLoading: false });
    }
  };

  loadSchedule = async () => {
    try {
      const res = await fetch(`${basePath}/api/v1/admin/pricing-agent/schedule`);
      const json = await res.json();
      if (!json.ok) throw new Error(json.error || "טעינת לוח הזמנים נכשלה");
      this.setState({ schedule: json.schedule });
    } catch (e) {
      this.setState({ error: e.message || String(e) });
    }
  };

  saveSchedule = async () => {
    const { schedule } = this.state;
    this.setState({ scheduleSaving: true, error: null });
    try {
      const res = await fetch(`${basePath}/api/v1/admin/pricing-agent/schedule`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          enabled: schedule.enabled,
          intervalDays: Number(schedule.intervalDays),
          time: schedule.time,
        }),
      });
      const json = await res.json();
      if (!json.ok) throw new Error(json.error || "שמירת לוח הזמנים נכשלה");
      this.setState({ schedule: json.schedule });
    } catch (e) {
      this.setState({ error: e.message || String(e) });
    } finally {
      this.setState({ scheduleSaving: false });
    }
  };

  startRun = async () => {
    const { from, to, baselineFrom, baselineTo, limit, includeCompetitors, maxChangePct } = this.state;
    this.setState({ error: null });
    try {
      const res = await fetch(`${basePath}/api/v1/admin/pricing-agent/run`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          from,
          to,
          baselineFrom,
          baselineTo,
          limit,
          includeCompetitors,
          maxChangePct,
        }),
      });
      const json = await res.json();
      if (!json.ok) throw new Error(json.error || "ההרצה נכשלה");
      await this.loadRuns();
      this.selectRun(json.runId);
    } catch (e) {
      this.setState({ error: e.message || String(e) });
    }
  };

  selectRun = async (runId) => {
    this.setState({ selectedRunId: runId, runLoading: true, selectedRun: null, selection: {}, error: null });
    try {
      const res = await fetch(`${basePath}/api/v1/admin/pricing-agent/runs/${runId}`);
      const json = await res.json();
      if (!json.ok) throw new Error(json.error || "טעינת ההרצה נכשלה");
      const run = json.run;
      const selection = {};
      const recs = (run && run.recommendations) || [];
      recs.forEach((r) => {
        if (isActionable(r)) selection[r.ean] = false;
      });
      this.setState({ selectedRun: run, selection });
    } catch (e) {
      this.setState({ error: e.message || String(e) });
    } finally {
      this.setState({ runLoading: false });
    }
  };

  setAllSelection = (checked) => {
    this.setState((prev) => {
      const selection = { ...prev.selection };
      Object.keys(selection).forEach((ean) => {
        selection[ean] = checked;
      });
      return { selection };
    });
  };

  toggleSelection = (ean) => {
    this.setState((prev) => ({
      selection: { ...prev.selection, [ean]: !prev.selection[ean] },
    }));
  };

  applySelected = async () => {
    const { selectedRunId, selection } = this.state;
    const eans = Object.keys(selection).filter((ean) => selection[ean]);
    if (!eans.length) return;

    this.setState({ applyLoading: true, error: null });
    try {
      const res = await fetch(`${basePath}/api/v1/admin/pricing-agent/runs/${selectedRunId}/apply`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mode: "selected", eans }),
      });
      const json = await res.json();
      if (!json.ok) throw new Error(json.error || "ההחלה נכשלה");
      await this.selectRun(selectedRunId);
    } catch (e) {
      this.setState({ error: e.message || String(e) });
    } finally {
      this.setState({ applyLoading: false });
    }
  };

  rollbackApply = async (applyId) => {
    const { selectedRunId } = this.state;
    this.setState({ applyLoading: true, error: null });
    try {
      const res = await fetch(
        `${basePath}/api/v1/admin/pricing-agent/runs/${selectedRunId}/rollback/${applyId}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({}),
        }
      );
      const json = await res.json();
      if (!json.ok) throw new Error(json.error || "הביטול נכשל");
      await this.selectRun(selectedRunId);
    } catch (e) {
      this.setState({ error: e.message || String(e) });
    } finally {
      this.setState({ applyLoading: false });
    }
  };

  renderRuns() {
    const { classes } = this.props;
    const { runs, runsLoading, selectedRunId } = this.state;
    return (
      <Paper className={classes.section}>
        <Box p={2}>
          <Typography variant="h6">{labels.runsTitle}</Typography>
        </Box>
        <Divider />
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{labels.created}</TableCell>
                <TableCell>{labels.status}</TableCell>
                <TableCell>{labels.trigger}</TableCell>
                <TableCell>{labels.range}</TableCell>
                <TableCell>{labels.limit}</TableCell>
                <TableCell>{labels.action}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {runsLoading ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <div className={classes.loading}>
                      <CircularProgress size={18} />
                      <Typography variant="body2">{labels.loadingRuns}</Typography>
                    </div>
                  </TableCell>
                </TableRow>
              ) : runs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    {labels.noRuns}
                  </TableCell>
                </TableRow>
              ) : (
                runs.map((r) => (
                  <TableRow key={r._id} selected={selectedRunId === String(r._id)}>
                    <TableCell className={classes.mono}>
                      {r.createdAt ? new Date(r.createdAt).toLocaleString() : "—"}
                    </TableCell>
                    <TableCell>{mapped(labels.statusMap, r.status)}</TableCell>
                    <TableCell>{mapped(labels.triggerMap, r.trigger)}</TableCell>
                    <TableCell className={classes.mono}>
                      {r.range?.from}..{r.range?.to}
                    </TableCell>
                    <TableCell>{r.config?.limit || "—"}</TableCell>
                    <TableCell>
                      <Button size="small" color="primary" onClick={() => this.selectRun(String(r._id))}>
                        {labels.view}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    );
  }

  renderRunDetail() {
    const { classes } = this.props;
    const { selectedRun, runLoading, selection, applyLoading } = this.state;
    if (runLoading) {
      return (
        <Paper className={classes.section}>
          <Box p={2} className={classes.loading}>
            <CircularProgress size={18} />
            <Typography variant="body2">{labels.loadingRun}</Typography>
          </Box>
        </Paper>
      );
    }
    if (!selectedRun) return null;

    const recs = (selectedRun.recommendations || []).slice().sort((a, b) => {
      const rank = { decrease: 0, increase: 1, keep: 2, skip: 3 };
      return (rank[a.action] ?? 9) - (rank[b.action] ?? 9);
    });
    const actionable = recs.filter(isActionable);
    const selectedCount = Object.keys(selection).filter((ean) => selection[ean]).length;

    return (
      <Paper className={classes.section}>
        <Box p={2}>
          <Typography variant="h6">{labels.runDetails}</Typography>
          <Typography variant="body2" color="textSecondary" className={classes.mono}>
            {selectedRun._id} • {mapped(labels.statusMap, selectedRun.status)} • {selectedRun.range?.from}..{selectedRun.range?.to}
          </Typography>
          <Box mt={2}>
            <Typography variant="subtitle2">{labels.summary}</Typography>
            <pre style={{ whiteSpace: "pre-wrap", margin: 0 }}>{selectedRun.summary || "—"}</pre>
          </Box>

          {selectedRun.error && (
            <Box mt={2}>
              <Typography color="error">{selectedRun.error}</Typography>
            </Box>
          )}

          <Box mt={2} display="flex" alignItems="center" gridGap={12} flexWrap="wrap">
            <Button size="small" variant="outlined" onClick={() => this.setAllSelection(true)} disabled={!actionable.length}>
              {labels.selectAll}
            </Button>
            <Button size="small" variant="outlined" onClick={() => this.setAllSelection(false)} disabled={!actionable.length}>
              {labels.clearSelection}
            </Button>
            <Typography variant="body2" color="textSecondary">
              {actionable.length} {labels.proposed} · {selectedCount} {labels.selectedCount}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={this.applySelected}
              disabled={applyLoading || selectedCount === 0}
            >
              {labels.applySelected}
            </Button>
            {applyLoading && <CircularProgress size={18} />}
          </Box>
        </Box>
        <Divider />

        <Box p={2}>
          {recs.length === 0 ? (
            <Typography variant="body2" color="textSecondary" align="center">
              {labels.noRecommendations}
            </Typography>
          ) : (
            recs.map((r) => {
              const product = r.snapshot?.product || {};
              const reasons = Array.isArray(r.reasons) ? r.reasons.filter(Boolean) : [];
              const actionableRow = isActionable(r);
              const recentLine = r.outcome?.before || r.outcome?.after
                ? `${labels.vitals}: ${countsLine(r.outcome.before)} ← ${countsLine(r.outcome.after)}`
                : r.recent
                  ? `${r.recent.days || 14} ${labels.days}: ${countsLine(r.recent)}`
                  : null;
              const peak = r.salesHistory?.peaks?.month
                ? `${labels.pastPeak}: ${labels.month} ${r.salesHistory.peaks.month.units} מול ${r.salesHistory.current?.monthUnits ?? 0}`
                : null;
              return (
                <div key={r.ean} className={`${classes.card} ${actionableRow ? "" : classes.cardMuted}`}>
                  <Checkbox
                    checked={Boolean(selection[r.ean])}
                    disabled={!actionableRow}
                    onChange={() => this.toggleSelection(r.ean)}
                  />
                  <img
                    src={productImageUrl(r)}
                    alt={product.brand || r.ean}
                    className={classes.cardThumb}
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = commons.getFallbackProductImage();
                    }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className={classes.productBrand}>
                      {[product.brand, product.size].filter(Boolean).join(" · ") || "—"}
                    </div>
                    <div className={classes.productMeta}>
                      {[product.name, r.ean].filter(Boolean).join(" · ")}
                    </div>
                    <Box mt={1}>
                      <Typography variant="body2">
                        <strong>{mapped(labels.actionMap, r.action)}</strong>
                        {" · "}
                        {r.currentPrice}
                        {r.suggestedPrice !== null && r.suggestedPrice !== r.currentPrice ? ` → ${r.suggestedPrice}` : ""}
                        {typeof r.confidence === "number" ? ` · ${labels.confidence} ${r.confidence.toFixed(2)}` : ""}
                      </Typography>
                    </Box>
                    <Typography variant="body2" style={{ marginTop: 8 }}>
                      <strong>{labels.why}</strong>
                    </Typography>
                    {reasons.length ? (
                      <ul className={classes.reasons}>
                        {reasons.map((reason) => (
                          <li key={`${r.ean}-${reason}`}>
                            <Typography variant="body2">{reason}</Typography>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        {labels.noReason}
                      </Typography>
                    )}
                    <div className={classes.facts}>
                      {`${labels.stock} ${r.snapshot?.stock ?? "—"}`}
                      {typeof r.unitNet === "number" ? ` · ${labels.unitNet} ${r.unitNet}` : ""}
                      {r.targetBand ? ` · ${labels.targetNet} ${r.targetBand.low}–${r.targetBand.high}` : ""}
                      {r.floorBand ? ` · ${labels.floor} ${r.floorBand.low}–${r.floorBand.high}` : ""}
                      {r.lastChange ? ` · ${labels.lastChange} ${r.lastChange.oldPrice ?? "—"} → ${r.lastChange.newPrice ?? "—"}` : ""}
                      {r.outcome?.status ? ` · ${mapped(labels.outcomeMap, r.outcome.status)}` : ""}
                      {recentLine ? ` · ${recentLine}` : ""}
                      {peak ? ` · ${peak}` : ""}
                      {r.salesHistory?.bestPrice ? ` · ₪${r.salesHistory.bestPrice}` : ""}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </Box>

        <Box p={2}>
          <Typography variant="subtitle2">{labels.applyHistory}</Typography>
          {(selectedRun.applyHistory || []).length === 0 ? (
            <Typography variant="body2" color="textSecondary">
              {labels.noApplyHistory}
            </Typography>
          ) : (
            (selectedRun.applyHistory || []).map((h) => (
              <Box key={h.applyId} mt={1} p={1} border={1} borderColor="rgba(0,0,0,0.12)" borderRadius={6}>
                <Typography variant="body2" className={classes.mono}>
                  {h.applyId} • {h.appliedAt ? new Date(h.appliedAt).toLocaleString() : "—"} •{" "}
                  {Array.isArray(h.changes) ? h.changes.length : 0} {labels.changes}
                  {h.rolledBackAt ? ` • ${labels.rolledBack} ${new Date(h.rolledBackAt).toLocaleString()}` : ""}
                </Typography>
                <Box mt={1}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => this.rollbackApply(h.applyId)}
                    disabled={applyLoading || Boolean(h.rolledBackAt)}
                  >
                    {labels.rollback}
                  </Button>
                </Box>
              </Box>
            ))
          )}
        </Box>
      </Paper>
    );
  }

  renderSchedule() {
    const { schedule, scheduleSaving } = this.state;
    const nextLabel = schedule.enabled && schedule.nextRunAt
      ? new Date(schedule.nextRunAt).toLocaleString("he-IL", { timeZone: "Asia/Jerusalem" })
      : labels.scheduleOff;
    return (
      <Paper className={this.props.classes.section}>
        <Box p={2}>
          <Typography variant="h6">{labels.scheduleTitle}</Typography>
          <Typography variant="body2" color="textSecondary">
            {labels.scheduleHelp}
          </Typography>
          <Box mt={2} display="flex" gridGap={12} flexWrap="wrap" alignItems="center">
            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(schedule.enabled)}
                  onChange={(e) => {
                    const enabled = e.target.checked;
                    this.setState((prev) => ({
                      schedule: { ...prev.schedule, enabled },
                    }));
                  }}
                />
              }
              label={labels.scheduleEnabled}
            />
            <TextField
              select
              label={labels.scheduleEvery}
              value={schedule.intervalDays}
              onChange={(e) => {
                const intervalDays = Number(e.target.value);
                this.setState((prev) => ({
                  schedule: { ...prev.schedule, intervalDays },
                }));
              }}
              size="small"
              style={{ minWidth: 140 }}
            >
              <MenuItem value={3}>{labels.every3}</MenuItem>
              <MenuItem value={7}>{labels.every7}</MenuItem>
              <MenuItem value={10}>{labels.every10}</MenuItem>
            </TextField>
            <TextField
              label={labels.scheduleTime}
              type="time"
              value={schedule.time || "09:00"}
              onChange={(e) => {
                const time = e.target.value;
                this.setState((prev) => ({
                  schedule: { ...prev.schedule, time },
                }));
              }}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
            <Button variant="contained" color="primary" onClick={this.saveSchedule} disabled={scheduleSaving}>
              {labels.scheduleSave}
            </Button>
            {scheduleSaving && <CircularProgress size={18} />}
          </Box>
          <Box mt={1}>
            <Typography variant="body2">
              {labels.scheduleNext}: {nextLabel}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {labels.scheduleMail} {schedule.notifyEmail}
            </Typography>
          </Box>
        </Box>
      </Paper>
    );
  }

  render() {
    const { classes } = this.props;
    const { from, to, baselineFrom, baselineTo, limit, includeCompetitors, maxChangePct, error } =
      this.state;

    return (
      <div className={classes.root}>
        <Paper>
          <Box p={2}>
            <Typography variant="h6">{labels.title}</Typography>
            <Typography variant="body2" color="textSecondary">
              {labels.subtitle}
            </Typography>

            <Box mt={2} display="flex" gridGap={12} flexWrap="wrap" alignItems="center">
              <TextField
                label={labels.from}
                type="date"
                value={from}
                onChange={(e) => this.setState({ from: e.target.value })}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
              <TextField
                label={labels.to}
                type="date"
                value={to}
                onChange={(e) => this.setState({ to: e.target.value })}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
              <TextField
                label={labels.baselineFrom}
                type="date"
                value={baselineFrom}
                onChange={(e) => this.setState({ baselineFrom: e.target.value })}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
              <TextField
                label={labels.baselineTo}
                type="date"
                value={baselineTo}
                onChange={(e) => this.setState({ baselineTo: e.target.value })}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
              <TextField
                label={labels.limit}
                type="number"
                value={limit}
                onChange={(e) => this.setState({ limit: parseInt(e.target.value, 10) || 80 })}
                size="small"
              />
              <TextField
                label={labels.maxChange}
                type="number"
                value={maxChangePct}
                onChange={(e) => this.setState({ maxChangePct: parseInt(e.target.value, 10) || 8 })}
                size="small"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeCompetitors}
                    onChange={(e) => this.setState({ includeCompetitors: e.target.checked })}
                  />
                }
                label={labels.competitors}
              />

              <Button variant="contained" color="primary" onClick={this.startRun}>
                {labels.runNow}
              </Button>
              <Button variant="text" onClick={this.loadRuns}>
                {labels.refresh}
              </Button>
            </Box>

            {error && (
              <Box mt={2}>
                <Typography color="error">{error}</Typography>
              </Box>
            )}
          </Box>
        </Paper>

        {this.renderSchedule()}
        {this.renderRuns()}
        {this.renderRunDetail()}
      </div>
    );
  }
}

PricingAgentPage.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(PricingAgentPage);


